import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as api from "../api";
import type {
  TaskGridPreferences, TaskGridPreferencesPatch, TaskGridResponse,
} from "../types";

export const taskGridKeys = {
  all: ["peopleTeamTaskGrid"] as const,
  grid: () => [...taskGridKeys.all, "grid"] as const,
  preferences: () => [...taskGridKeys.all, "preferences"] as const,
};

export function useTaskGridQuery() {
  return useQuery<TaskGridResponse>({
    queryKey: taskGridKeys.grid(),
    queryFn: api.fetchTaskGrid,
    staleTime: 1000 * 60 * 2,
    refetchInterval: 1000 * 60 * 5,
  });
}

export function useTaskGridPreferencesQuery() {
  return useQuery<TaskGridPreferences>({
    queryKey: taskGridKeys.preferences(),
    queryFn: api.fetchTaskGridPreferences,
    staleTime: 1000 * 60 * 10,
  });
}

export function useUpdateTaskGridPreferencesMutation() {
  const queryClient = useQueryClient();
  return useMutation<TaskGridPreferences, Error, TaskGridPreferencesPatch>({
    mutationFn: api.updateTaskGridPreferences,
    onMutate: async (patch) => {
      await queryClient.cancelQueries({ queryKey: taskGridKeys.preferences() });
      const previous = queryClient.getQueryData<TaskGridPreferences>(taskGridKeys.preferences());
      if (previous) {
        queryClient.setQueryData(taskGridKeys.preferences(), { ...previous, ...patch });
      }
      return { previous };
    },
    onError: (_err, _patch, ctx) => {
      const previous = (ctx as { previous?: TaskGridPreferences } | undefined)?.previous;
      if (previous) {
        queryClient.setQueryData(taskGridKeys.preferences(), previous);
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData(taskGridKeys.preferences(), data);
    },
  });
}
